import { useEffect, useState } from 'react';
import { Button } from '@vwfs-bronson/bronson-react';
import { AuthTokens } from '../../auth';
import { Agent, FesConfig, Job, Pagination, Team, getAgents, getTeams } from '../../fes';
import { JobDetailPanel } from './JobDetailPanel';
import { JobsTable } from './JobsTable';

export function JobsView({
  fesConfig,
  tokens,
  jobs,
  pagination,
  onPageChange,
}: {
  fesConfig: FesConfig;
  tokens: AuthTokens;
  jobs: Job[];
  pagination?: Pagination;
  onPageChange: (offset: number) => Promise<void>;
}) {
  const [selectedJob, setSelectedJob] = useState<Job | null>(null);
  const [agentsById, setAgentsById] = useState<Map<string, Agent>>(new Map());
  const [teamsById, setTeamsById] = useState<Map<string, Team>>(new Map());
  const [areLookupsLoading, setAreLookupsLoading] = useState(false);
  const [lookupError, setLookupError] = useState<string>();
  const [isRefreshing, setIsRefreshing] = useState(false);

  useEffect(() => {
    let cancelled = false;

    setAreLookupsLoading(true);
    setLookupError(undefined);

    const loadLookups = async () => {
      try {
        const [agentsResponse, teamsResponse] = await Promise.all([
          getAgents(fesConfig, tokens),
          getTeams(fesConfig, tokens),
        ]);

        if (cancelled) {
          return;
        }

        setAgentsById(new Map((agentsResponse.data || []).map(agent => [agent.agentId, agent])));
        setTeamsById(new Map((teamsResponse.data || []).map(team => [team.teamId, team])));
      } catch (error) {
        if (!cancelled) {
          setLookupError(error instanceof Error ? error.message : 'Unable to load agent and team names.');
        }
      } finally {
        if (!cancelled) {
          setAreLookupsLoading(false);
        }
      }
    };

    void loadLookups();

    return () => {
      cancelled = true;
    };
  }, [fesConfig, tokens]);

  const selectedAgentName = selectedJob?.agentId ? agentsById.get(selectedJob.agentId)?.name : undefined;
  const selectedTeamName = selectedJob?.teamId ? teamsById.get(selectedJob.teamId)?.name : undefined;

  const handleRefresh = async () => {
    if (isRefreshing) {
      return;
    }

    setIsRefreshing(true);
    try {
      await onPageChange(pagination?.offset ?? 0);
    } finally {
      setIsRefreshing(false);
    }
  };

  return (
    <>
      <div className="app-table-toolbar">
        <Button secondary icon="semantic-reload" disabled={isRefreshing} onClick={handleRefresh}>
          {isRefreshing ? 'Refreshing...' : 'Refresh'}
        </Button>
      </div>
      <JobsTable
        jobs={jobs}
        pagination={pagination}
        selectedJobId={selectedJob?.requestId}
        onSelectJob={setSelectedJob}
        onPageChange={onPageChange}
      />
      <JobDetailPanel
        job={selectedJob}
        agentName={selectedAgentName}
        teamName={selectedTeamName}
        lookupsLoading={areLookupsLoading}
        lookupError={lookupError}
        onClose={() => setSelectedJob(null)}
      />
    </>
  );
}
